












































import {
  defineComponent, ref, watch, useRoute, useContext, useRouter
} from '@nuxtjs/composition-api';
import { debounce } from 'lodash-es';
import { clickOutside } from '~/components/directives/click-outside/click-outside-directive';
import { useCms, useGtm } from '~/composables';
import Utils from '~/helpers/utils';
import SfSearchBar from '~/components/Sf/SfSearchBar.vue';
import { useConfig } from "~/composables";
import { getLocalizedPath } from "~/helpers/getLocalizedPath";

export default defineComponent({
  name: 'SearchBar',
  components: {
    SfSearchBar,
  },
  directives: { clickOutside },
  props: {
    name: {
      type: String,
      default: "",
    },
    isSearchOpen: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 12,
    },
    minTermLen: {
      type: Number,
      default: 1,
    },
  },
  emits: ['set-is-open', 'set-search-results', 'is-opened-nav'],
  setup(props, { emit }) {
    const { eventSearchResultsCTA, eventCustomlinksInteracted } = useGtm();
    const { config } = useConfig();
    const showTopNotification = Boolean(Number(config.value.show_top_notification));
    const { preventXSSAttacks, preventSqlAttacks } = Utils();
    const term = ref('');
    const isActive = ref(false);
    const route = useRoute();
    const router = useRouter();
    const context = useContext();
    const { searchCms } = useCms();
    const searchOptions = ref([]);
    const isShow = ref(false);
    const showSearch = () => {
      if (!props.isSearchOpen) {
        emit('set-is-open', true);
        if (document) {
          document.body.classList.add('miniSearech-active');
        }
      }
    };

    const hideSearch = () => {
      if (props.isSearchOpen) {
        emit('set-is-open', false);
        emit('set-search-results', null);
        if (document) {
          document.body.classList.remove('miniSearech-active');
        }
      }
    };

    const toggleSearch = () => {
      if (props.isSearchOpen) {
        hideSearch();
      } else {
        showSearch();
      }
    };

    const closeSearch = (event: MouseEvent) => {
      isActive.value = false;
      if (document) {
        const searchResultsEl = document.querySelector('.search');
        const closeTriggerElement = event.target as HTMLElement;

        if (!searchResultsEl?.contains(closeTriggerElement)) {
          hideSearch();
          term.value = '';
        }
      } else {
        hideSearch();
        term.value = '';
      }
    };

    const rawHandleSearch = async (searchTerm: string) => {
      term.value = preventXSSAttacks(searchTerm);
      if (term.value.length < props.minTermLen) return;
      if (!preventSqlAttacks(searchTerm)){
        const data = await searchCms({
           keyword: term.value,
           pageSize: 20
          });
        if (data['total_count'] > 0) {
          searchOptions.value = data['data'];
          isShow.value = true;
          isActive.value = true;
        } else {
          searchOptions.value = [];
          isShow.value = false;
        }
        eventSearchResultsCTA({
          "Search Category": "Global Search",
          "Label": "Top Search",
          "SearchTerm": term.value,
          "SearchResults": data['data'],
          "itemCount": data['total_count'],
        });
      }
    };
    const debouncedHandleSearch = debounce(rawHandleSearch, 300);

    const handleKeydownEnter = (searchTerm: string) => {
      debouncedHandleSearch.cancel();
      toSearchResult(searchTerm);
    };

    watch(route, () => {
      hideSearch();
      isActive.value = false;
      term.value = '';
    });

    const toSearchResult = (value) => {
      if (!preventSqlAttacks(value)){
      document.body.classList.remove('no-scroll');
      isActive.value = false;
      if (value) {
        emit('is-opened-nav', false);
        router.push(
          context.localeRoute({ name: 'search', query: { query: String(value) } }),
        );
      }
      }
    };

    const selectOption = (option) => {
      term.value = option.title;
      isShow.value = false;
      toSearchResult(term.value);
    };

    const navigateToDetailPage = (option) => {
      const routePath = option.url_key;
      isActive.value = false;
      if (routePath) {
        emit('is-opened-nav', false);
        clearInput();
        closeSearchOption();
        router.push(
          getLocalizedPath(routePath, context.app.i18n),
        );
      }
      showTopNotification && (document.getElementById('__layout').className = 'show-top-notification');
    };

    const closeSearchOption = () => {
      isShow.value = false;
    };

    const clearInput = () => {
      term.value = '';
      isActive.value = false;
    };

    return {
      clearInput,
      closeSearchOption,
      selectOption,
      searchOptions,
      closeSearch,
      showSearch,
      hideSearch,
      toggleSearch,
      rawHandleSearch,
      debouncedHandleSearch,
      handleKeydownEnter,
      term,
      isActive,
      isShow,
      toSearchResult,
      navigateToDetailPage,
      eventCustomlinksInteracted,
    };
  },
});

